import { b2Vec2 } from "./box2d-compat.js";
import { FlashLevelBaseRuntime } from "./flash-level-base-runtime.js";

const GOAL_SYMBOL = Object.freeze({
  imageWidth: 25.2,
  imageHeight: 46.5,
  imageOffsetX: -1.0,
  imageOffsetY: -15.2,
});

const CHECKPOINT_SYMBOL = Object.freeze({
  imageWidth: 10.8,
  imageHeight: 21.1,
  imageOffsetX: -0.5,
  imageOffsetY: -0.5,
});

const GREEN_CHECK_SYMBOL = Object.freeze({
  imageWidth: 11.0,
  imageHeight: 11.0,
  imageOffsetX: -5.0,
  imageOffsetY: -5.55,
});

const LEVEL9_BODY_LAYOUT_FIX = Object.freeze({
  firstBigPlatform: { x: -1346.9, y: -286.95 },
  lowPlatform: { x: 1256.9, y: 129.95 },
  upPlatform: { x: 1415.9, y: -71.0 },
  middlePlatform: { x: 1492.9, y: 103.05 },
  finishPlatform: { x: 2930.7, y: 131.1 },
  low2Platform: { x: 1651.55, y: 133.95 },
});

const LEVEL9_DYNAMIC_SYMBOLS = Object.freeze({
  firstCrank: {
    imagePath: "port/generated/symbols/209/png/1.png",
    width: 366.3,
    height: 366.3,
    offsetX: -278.2,
    offsetY: -158.75,
  },
  secondCrank: {
    imagePath: "port/generated/symbols/213/png/1.png",
    width: 402.75,
    height: 231.65,
    offsetX: -245.55,
    offsetY: -136.15,
  },
  boomCrank: {
    imagePath: "port/generated/symbols/215/png/1.png",
    width: 70.25,
    height: 31.0,
    offsetX: -64.75,
    offsetY: -15.5,
  },
  greenPlatform: {
    imagePath: "port/generated/symbols/221/png/1.png",
    width: 210.4,
    height: 116.3,
    offsetX: -57.85,
    offsetY: -42.2,
  },
  jumpBall1: {
    imagePath: "port/generated/symbols/234/png/1.png",
    width: 191.65,
    height: 191.65,
    offsetX: -58.85,
    offsetY: -60.5,
  },
  jumpBall2: {
    imagePath: "port/generated/symbols/234/png/1.png",
    width: 191.65,
    height: 191.65,
    offsetX: -58.85,
    offsetY: -60.5,
  },
  jumpBall3: {
    imagePath: "port/generated/symbols/234/png/1.png",
    width: 191.65,
    height: 191.65,
    offsetX: -58.85,
    offsetY: -60.5,
  },
  bridgeElement1: {
    imagePath: "port/generated/symbols/241/png/1.png",
    width: 193.15,
    height: 138.75,
    offsetX: -78.6,
    offsetY: -72.0,
  },
  bridgeElement2: {
    imagePath: "port/generated/symbols/241/png/1.png",
    width: 193.15,
    height: 138.75,
    offsetX: -78.6,
    offsetY: -72.0,
  },
  bridgeElement3: {
    imagePath: "port/generated/symbols/241/png/1.png",
    width: 193.15,
    height: 138.75,
    offsetX: -78.6,
    offsetY: -72.0,
  },
  bridgeElement4: {
    imagePath: "port/generated/symbols/241/png/1.png",
    width: 193.15,
    height: 138.75,
    offsetX: -78.6,
    offsetY: -72.0,
  },
  bridgeElement5: {
    imagePath: "port/generated/symbols/241/png/1.png",
    width: 193.15,
    height: 138.75,
    offsetX: -78.6,
    offsetY: -72.0,
  },
  bridgeElement6: {
    imagePath: "port/generated/symbols/241/png/1.png",
    width: 193.15,
    height: 138.75,
    offsetX: -78.6,
    offsetY: -72.0,
  },
  bridgeElement7: {
    imagePath: "port/generated/symbols/241/png/1.png",
    width: 193.15,
    height: 138.75,
    offsetX: -78.6,
    offsetY: -72.0,
  },
  bridgeElement8: {
    imagePath: "port/generated/symbols/241/png/1.png",
    width: 193.15,
    height: 138.75,
    offsetX: -78.6,
    offsetY: -72.0,
  },
  bridgeElement9: {
    imagePath: "port/generated/symbols/241/png/1.png",
    width: 193.15,
    height: 138.75,
    offsetX: -78.6,
    offsetY: -72.0,
  },
  bridgeElement10: {
    imagePath: "port/generated/symbols/241/png/1.png",
    width: 193.15,
    height: 138.75,
    offsetX: -78.6,
    offsetY: -72.0,
  },
  killRollBall: {
    imagePath: "port/generated/symbols/246/png/1.png",
    width: 51.0,
    height: 51.05,
    offsetX: -0.5,
    offsetY: -0.5,
  },
});

function drawSymbolImage(ctx, image, symbol, x, y, rotationDeg = 0) {
  if (!image?.complete || !symbol) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((rotationDeg * Math.PI) / 180);
  ctx.drawImage(image, symbol.offsetX, symbol.offsetY, symbol.width, symbol.height);
  ctx.restore();
}

function drawAnimatedFrame(ctx, image, symbol, x, y) {
  if (!image?.complete || !symbol) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  ctx.drawImage(image, symbol.imageOffsetX, symbol.imageOffsetY, symbol.imageWidth, symbol.imageHeight);
  ctx.restore();
}

export class LevelNineRuntime extends FlashLevelBaseRuntime {
  buildLevel() {
    if (this.assetsEnabled) {
      this.stageImage.src = "port/generated/levels_fg/level_9/1.visual.runtime.svg";
      this.level9DynamicImages = Object.fromEntries(
        Object.entries(LEVEL9_DYNAMIC_SYMBOLS).map(([name, symbol]) => {
          const image = new Image();
          image.src = symbol.imagePath;
          return [name, image];
        }),
      );
      this.greenCheckFrames = [1, 2].map((frame) => {
        const image = new Image();
        image.src = `port/generated/symbols/96/png/DefineSprite_96/${frame}.png`;
        return image;
      });
    } else {
      this.level9DynamicImages = {};
      this.greenCheckFrames = [];
    }

    for (const [name, fix] of Object.entries(LEVEL9_BODY_LAYOUT_FIX)) {
      const layout = this.levelData.layout.namedChildren[name];
      if (layout) {
        layout.x = fix.x;
        layout.y = fix.y;
      }
    }

    this.setNamedObjectFrame("greenCheck1", 1);
    this.setNamedObjectFrame("greenCheck2", 1);
    this.setNamedObjectFrame("greenCheck3", 1);

    this.firstCrankBody = this.createBody("firstCrank", "Polygon", 1, 1, 0.2, [[[6.8, -7.65], [6.8, 7.35], [-73.2, 7.35], [-73.2, -7.65]]]);
    const secondCrankBody = this.createBody("secondCrank", "Polygon", 1, 1, 0.2, [[[7.2, -6.6], [7.2, 8.4], [-142.8, 8.4], [-142.8, -6.6]]]);
    this.boomCrankBody = this.createBody("boomCrank", "Polygon", 1, 1, 0.2, [[[5, -15], [5, 15], [-53.5, 15], [-64.25, -0.25], [-53.5, -15]]]);
    this.createBody("firstBigPlatform", "Polygon", 0, 1, 0.2, this.levelData.bodies.find((body) => body.name === "firstBigPlatform").data);
    this.greenPlatformBody = this.createBody("greenPlatform", "Polygon", 1, 1, 0.2, [[[-49, -5], [49, -5], [49, 5], [-49, 5]]]);
    const lowPlatformBody = this.createBody("lowPlatform", "Polygon", 0, 1, 0.2, [[[-581.95, -109.1], [-522.95, -109.1], [-522.95, -9], [-705.95, -35]], [[-795.95, -35], [-705.95, -35], [-522.95, -9], [-795.95, -9]]]);
    const low2PlatformBody = this.createBody("low2Platform", "Polygon", 0, 1, 0.2, [[[286.95, -10.95], [399.95, -10.95], [286.95, 14.05]], [[262.95, 14.05], [286.95, 14.05], [262.95, 37.05]], [[286.95, 14.05], [399.95, -10.95], [262.95, 37.05]], [[239.95, 37.05], [262.95, 37.05], [239.95, 59.05]], [[262.95, 37.05], [399.95, -10.95], [239.95, 59.05]], [[215, 59.05], [239.95, 59.05], [215, 82]], [[239.95, 59.05], [399.95, -10.95], [399.95, 113], [215, 82]], [[-399.95, -112.95], [-288, -112.95], [-318, -13], [-399.95, -13]], [[-288, -112.95], [-288, 82], [-318, 113], [-318, -13]], [[-288, 82], [215, 82], [399.95, 113], [-318, 113]]]);
    this.createBody("upPlatform", "Polygon", 0, 1, 0.2, [[[163, -58], [163, -26], [29, 1.5], [-162.95, -58]], [[-162.95, 47], [-162.95, -58], [29, 1.5], [29, 58], [-150.95, 58]]]);
    this.createBody("middlePlatform", "Polygon", 0, 1, 0.2, [[[-48.6, -81.9], [-48.6, -26.05], [-96, 81.9], [-96, -81.9]], [[-96, 81.9], [-48.6, -26.05], [96, -9.05], [96, 81.9]]]);
    this.createBody("finishPlatform", "Polygon", 0, 1, 0.2, [[[-58, -12.5], [58, -12.5], [58, 12.5], [-58, 12.5]]]);
    const bridgeBodies = [];
    for (let index = 1; index <= 10; index += 1) {
      bridgeBodies.push(this.createBody(`bridgeElement${index}`, "Polygon", 1, 1, 0.2, [[[-4, -4], [56, -4], [56, 4], [-4, 4]]]));
    }
    this.killRollBallBody = this.createBody("killRollBall", "Circle", 1, 1, 0.2, [50]);
    const jumpBall1 = this.createBody("jumpBall1", "Circle", 1, 1, 0.2, [75]);
    const jumpBall2 = this.createBody("jumpBall2", "Circle", 1, 1, 0.2, [75]);
    const jumpBall3 = this.createBody("jumpBall3", "Circle", 1, 1, 0.2, [75]);

    this.addPrismaticJoint(this.greenPlatformBody, this.ground, this.greenPlatformBody.GetWorldCenter(), new b2Vec2(1, 0));
    this.addRevoluteJoint(lowPlatformBody, bridgeBodies[0], bridgeBodies[0].GetPosition());
    for (let index = 0; index < bridgeBodies.length - 1; index += 1) {
      this.addRevoluteJoint(bridgeBodies[index], bridgeBodies[index + 1], bridgeBodies[index + 1].GetPosition());
    }
    this.addRevoluteJoint(bridgeBodies[9], low2PlatformBody, new b2Vec2(bridgeBodies[9].GetPosition().x + 50 / 30, bridgeBodies[9].GetPosition().y));
    this.addRevoluteJoint(this.firstCrankBody, this.ground, this.firstCrankBody.GetPosition(), { motorSpeed: 1.8 * Math.PI, maxMotorTorque: 5000, enableMotor: true });
    this.addRevoluteJoint(secondCrankBody, this.firstCrankBody, secondCrankBody.GetPosition());
    this.addRevoluteJoint(this.boomCrankBody, secondCrankBody, this.boomCrankBody.GetPosition());
    this.addPrismaticJoint(this.boomCrankBody, this.ground, this.boomCrankBody.GetWorldCenter(), new b2Vec2(1, 0));
    this.addDistanceJoint(jumpBall1, this.ground, new b2Vec2(2236 / 30, 63 / 30), new b2Vec2(2172 / 30, -56 / 30));
    this.addDistanceJoint(jumpBall2, this.ground, new b2Vec2(2370 / 30, 63 / 30), new b2Vec2(2435 / 30, -56 / 30));
    this.addDistanceJoint(jumpBall3, this.ground, new b2Vec2(2761 / 30, 63 / 30), new b2Vec2(2698 / 30, -56 / 30));
    this.greenPlatformBodyDirection = 1;
    this.killRollBallDirection = 1;
  }

  levelUpdate() {
    if (this.player.y > 530 || this.contactListener.playerContactBodies.indexOf(this.killRollBallBody) !== -1 || this.contactListener.playerContactBodies.indexOf(this.boomCrankBody) !== -1) {
      if (this.player.isLive()) {
        this.startDeath();
      }
    }
    const greenPlatform = this.getDisplayLayout("greenPlatform");
    if (greenPlatform.x < 55) {
      this.greenPlatformBodyDirection = 1;
    }
    if (greenPlatform.x > 387) {
      this.greenPlatformBodyDirection = -1;
    }
    this.greenPlatformBody?.SetLinearVelocity(new b2Vec2(2 * this.greenPlatformBodyDirection, 0));
    for (const name of ["greenCheck1", "greenCheck2", "greenCheck3"]) {
      if (this.hitNamedObject(name) && this.getNamedObjectFrame(name) === 1 && this.greenPlatformBody) {
        this.setNamedObjectFrame(name, 2);
        this.destroyBody(this.greenPlatformBody);
        this.greenPlatformBody = null;
      }
    }
    const killRollBall = this.getDisplayLayout("killRollBall");
    if (killRollBall.x < 740) {
      this.killRollBallDirection = 1;
    }
    if (killRollBall.x > 1200) {
      this.killRollBallDirection = -1;
    }
    this.killRollBallBody.SetAngularVelocity(5 * this.killRollBallDirection);
  }

  render(ctx, stageWidth, stageHeight, options = {}) {
    const zoom = Math.min(4, Math.max(0.1, Number(options.zoom) || 1));
    ctx.save();
    ctx.clearRect(0, 0, stageWidth, stageHeight);
    ctx.translate(stageWidth / 2, stageHeight / 2);
    ctx.scale(zoom, zoom);
    ctx.translate(-stageWidth / 2, -stageHeight / 2);

    if (this.backgroundImage?.complete && this.backgroundStart && this.backgroundSymbol?.layout) {
      const sym = this.backgroundSymbol;
      const bx = this.backgroundStart.x - this.camera.x / 10;
      const by = this.backgroundStart.y - this.camera.y / 10;
      ctx.save();
      ctx.translate(bx, by);
      if (sym.pngLayout) {
        const scaleX = sym.layout.imageWidth / sym.pngLayout.sourceWidth;
        const scaleY = sym.layout.imageHeight / sym.pngLayout.sourceHeight;
        ctx.drawImage(
          this.backgroundImage,
          sym.pngLayout.cropX, sym.pngLayout.cropY,
          sym.pngLayout.cropWidth, sym.pngLayout.cropHeight,
          sym.layout.imageOffsetX + sym.pngLayout.cropX * scaleX,
          sym.layout.imageOffsetY + sym.pngLayout.cropY * scaleY,
          sym.pngLayout.cropWidth * scaleX,
          sym.pngLayout.cropHeight * scaleY,
        );
      } else {
        ctx.drawImage(this.backgroundImage, sym.layout.imageOffsetX, sym.layout.imageOffsetY);
      }
      ctx.restore();
    }

    ctx.translate(this.camera.x, this.camera.y);

    if (this.stageImage?.complete) {
      const { layout } = this.levelData;
      ctx.drawImage(this.stageImage, layout.imageOffsetX, layout.imageOffsetY, layout.imageWidth, layout.imageHeight);
    }

    for (const name of [
      "firstCrank",
      "secondCrank",
      "boomCrank",
      "jumpBall1",
      "jumpBall2",
      "jumpBall3",
      "bridgeElement1",
      "bridgeElement2",
      "bridgeElement3",
      "bridgeElement4",
      "bridgeElement5",
      "bridgeElement6",
      "bridgeElement7",
      "bridgeElement8",
      "bridgeElement9",
      "bridgeElement10",
      "killRollBall",
    ]) {
      const layout = this.getDisplayLayout(name);
      if (!layout) {
        continue;
      }
      drawSymbolImage(
        ctx,
        this.level9DynamicImages[name],
        LEVEL9_DYNAMIC_SYMBOLS[name],
        layout.x,
        layout.y,
        layout.rotation ?? 0,
      );
    }

    if (this.greenPlatformBody) {
      const greenPlatformLayout = this.getDisplayLayout("greenPlatform");
      drawSymbolImage(
        ctx,
        this.level9DynamicImages.greenPlatform,
        LEVEL9_DYNAMIC_SYMBOLS.greenPlatform,
        greenPlatformLayout.x,
        greenPlatformLayout.y,
        greenPlatformLayout.rotation ?? 0,
      );
    }

    for (const checkName of ["greenCheck1", "greenCheck2", "greenCheck3"]) {
      const layout = this.getDisplayLayout(checkName);
      const frame = this.greenCheckFrames[this.getNamedObjectFrame(checkName) - 1];
      drawAnimatedFrame(ctx, frame, GREEN_CHECK_SYMBOL, layout.x, layout.y);
    }

    for (const checkpointState of this.checkpoints) {
      const checkpointFrame = this.checkpointFrames[checkpointState.currentFrame - 1];
      drawAnimatedFrame(ctx, checkpointFrame, CHECKPOINT_SYMBOL, checkpointState.x, checkpointState.y);
    }

    const goalFrame = this.goalFrames[this.goalCurrentFrame - 1];
    drawAnimatedFrame(ctx, goalFrame, GOAL_SYMBOL, this.goalPosition.x, this.goalPosition.y);

    if (this.player?.isLive?.()) {
      ctx.save();
      ctx.translate(this.player.x, this.player.y);
      ctx.rotate(this.player.body.GetAngle());
      if (this.playerImage?.complete) {
        ctx.drawImage(this.playerImage, -10.5, -10.5);
      }
      ctx.restore();
    }

    ctx.fillStyle = "#d32f2f";
    for (const particle of this.particles) {
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, 4, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }
}
